<template>
    <div class="data-widget-card bg-white">
        <div class="q-pa-none dashboard-card--header">
            <p class="data-widget-title">
                {{ title }}
            </p>
            <p
                v-if="relativeDifference"
                class="data-widget-difference"
                :class="getRelativeDifferenceClass"
            >
                {{ getRelativeDifferenceSymbol }}
                {{
                    relativeDifference
                        ? relativeDifference.toFixed(1) + '%'
                        : ''
                }}
            </p>
        </div>
        <div
            class="text-center q-pa-none data-widget-card--body"
            :class="getColor"
        >
            {{ parseFloat(value.toFixed(1)) || 0
            }}<span v-if="unit">{{ unit }}</span>
        </div>
    </div>
</template>
<script>
export default {
    name: 'DataWidget',
    props: {
        title: {
            type: String,
            required: true
        },
        value: {
            type: Number,
            required: true
        },
        unit: {
            type: String,
            required: false,
            default: null
        },
        relativeDifference: {
            type: Number,
            required: false,
            default: null
        },
        relativeDifferenceIncreaseIsPositive: {
            type: Boolean,
            required: false,
            default: false
        },
        hasColorRange: {
            type: Boolean,
            required: false,
            default: false
        },
        colorRanges: {
            type: Array[Object],
            required: false,
            default: () => [
                { low: 0, high: 50, color: 'positive' },
                { low: 50, high: 75, color: 'warning' },
                { low: 75, high: 100, color: 'negative' }
            ],
            validator(propValue) {
                // Check if props is an array
                if (!Array.isArray(propValue)) {
                    return false;
                }

                // Check if item is an object
                function validateItemObject(item) {
                    if (!(typeof item === 'object')) {
                        return false;
                    }
                    return true;
                }

                // Check if item has properties
                function validateItemProperties(item) {
                    if (
                        !Object.hasOwn(item, 'low') ||
                        !Object.hasOwn(item, 'high') ||
                        !Object.hasOwn(item, 'color')
                    ) {
                        return false;
                    }
                    return true;
                }

                // Check if range is a number and if it`s overlap
                let previousHigh = 0;
                function validateItemRange(item) {
                    if (!(typeof item.low === 'number')) {
                        return false;
                    }
                    if (!(typeof item.high === 'number')) {
                        return false;
                    }
                    if (item.low > item.high || item.low != previousHigh) {
                        return false;
                    }
                    previousHigh = item.high;
                    return true;
                }

                // Check if item has valid colors
                const validColors = [
                    'positive',
                    'warning',
                    'negative',
                    'neutral'
                ];
                function validateItemColors(item) {
                    if (!validColors.includes(item.color)) {
                        return false;
                    }
                    return true;
                }

                for (const el of propValue) {
                    if (!validateItemObject(el)) {
                        return false;
                    }
                    if (!validateItemProperties(el)) {
                        return false;
                    }
                    if (!validateItemRange(el)) {
                        return false;
                    }
                    if (!validateItemColors(el)) {
                        return false;
                    }
                }
                return true;
            }
        }
    },
    computed: {
        /**
         * Get the class of the relative difference
         * @return {String} The class of the relative difference
         */
        getRelativeDifferenceClass() {
            if (this.relativeDifferenceIncreaseIsPositive === true) {
                if (this.relativeDifference < 0) {
                    return 'data-widget-color--negative';
                } else if (this.relativeDifference > 0) {
                    return 'data-widget-color--positive';
                } else {
                    return 'data-widget-color--neutral';
                }
            } else {
                if (this.relativeDifference < 0) {
                    return 'data-widget-color--positive';
                } else if (this.relativeDifference > 0) {
                    return 'data-widget-color--negative';
                } else {
                    return 'data-widget-color--neutral';
                }
            }
        },
        /**
         * Get the symbol of the relative difference
         * @return {String} The symbol of the relative difference
         */
        getRelativeDifferenceSymbol() {
            if (this.relativeDifference < 0) {
                return '▼';
            } else if (this.relativeDifference > 0) {
                return '▲';
            } else {
                return '';
            }
        },
        /**
         * Get the color of the data widget
         * @return {String} The color of the data widget
         */
        getColor() {
            if (this.hasColorRange) {
                let color = 'neutral';
                this.colorRanges.forEach(element => {
                    if (
                        this.value >= element.low &&
                        this.value <= element.high
                    ) {
                        color = element.color;
                    }
                });
                return `data-widget-color--${color}`;
            }
            return null;
        }
    }
};
</script>

<style lang="scss" scoped>
@import '@css/quasar.variables.scss';

$title-color: #444;

.dashboard-card--header {
    color: $title-color;
    font-size: 20px;
    font-weight: 600;
    display: flex;
    justify-content: space-between;
}

.data-widget-card {
    border: 1px solid $grey-4;
    border-radius: 5px;
    padding: 15px 20px;
}

.data-widget-card--body {
    font-size: 40px;
    font-weight: 500;
}

.data-widget-color--positive {
    color: $positive;
}

.data-widget-color--warning {
    color: $warning;
}

.data-widget-color--negative {
    color: $negative;
}

.data-widget-color--neutral {
    color: $secondary;
}

@media (max-width: $breakpoint-md-max) {
    .data-widget-title {
        width: 100%;
        font-size: 18px;
    }

    .data-widget-card--body {
        font-size: 30px;
    }

    .dashboard-card--header p {
        margin-bottom: 5px;
    }

    .data-widget-difference {
        text-align: right;
        width: 110px;
        font-size: 16px;
    }
}
</style>
